import AdminPanelSettingsIcon from '@material-design-icons/svg/filled/admin_panel_settings.svg?react';
import ArrowBackIcon from '@material-design-icons/svg/filled/arrow_back.svg?react';
import AltRouteIcon from '@material-design-icons/svg/filled/alt_route.svg?react';
import InfoIcon from '@material-design-icons/svg/filled/info.svg?react';
import RuleIcon from '@material-design-icons/svg/filled/rule.svg?react';
import SaveIcon from '@material-design-icons/svg/filled/save.svg?react';
import TuneIcon from '@material-design-icons/svg/filled/tune.svg?react';
import VisibilityOffIcon from '@material-design-icons/svg/filled/visibility_off.svg?react';
import { isLeft, isRight } from 'fp-ts/Either';
import { use, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { ManagementPageLayout } from '../components/management/ManagementPageLayout';
import { RejectionReasonManagementSection } from '../components/RejectionReasonManagement/RejectionReasonManagementSection';
import { SearchableMultiSelect } from '../components/SearchableMultiSelect';
import { Button } from '../components/ui/button';
import { TRPCContext } from '../contexts/TRPC';
import { useAsyncData } from '../hooks/useAsyncData';
import { AuthStateType } from '../hooks/useAuth';
import { usePageTitle } from '../hooks/usePageTitle';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { ModeratorPresetManagementSection } from '../components/ModeratorPresetManagement/ModeratorPresetManagementSection';
import { captureAndReportError } from '../utils/errorReporting';
import { canAccessAdminPages } from '../utils/roleUtils';

interface ModeratorSettingsDapp {
  id: string;
  dappName: string;
  packageName: string | null;
  status: string;
  hiddenFromModerators: boolean;
}

interface ModeratorSettingsUser {
  id: string;
  email: string;
  fullName: string;
  role: 'base' | 'moderator' | 'admin' | 'superadmin';
}

interface ModeratorSettingsResponse {
  dapps: ModeratorSettingsDapp[];
  hiddenDappIds: string[];
  assignableUsers: ModeratorSettingsUser[];
  rejectionFailureAssigneeUserId: string | null;
  rejectionFailureAssigneeSource: 'configured' | 'fallback' | 'unset';
  fallbackRejectionFailureAssigneeEmail: string;
}

const roleLabel: Record<ModeratorSettingsUser['role'], string> = {
  base: 'Base',
  moderator: 'Moderator',
  admin: 'Admin',
  superadmin: 'Superadmin',
};

const sourceStyles: Record<
  ModeratorSettingsResponse['rejectionFailureAssigneeSource'],
  { label: string; dot: string; text: string; ring: string }
> = {
  configured: {
    label: 'Configured',
    dot: 'bg-teal-400',
    text: 'text-teal-200',
    ring: 'bg-teal-500/10 ring-teal-500/30',
  },
  fallback: {
    label: 'Fallback active',
    dot: 'bg-amber-400',
    text: 'text-amber-200',
    ring: 'bg-amber-500/10 ring-amber-500/30',
  },
  unset: {
    label: 'Unset',
    dot: 'bg-neutral-500',
    text: 'text-neutral-300',
    ring: 'bg-neutral-800/60 ring-neutral-700/60',
  },
};

function SettingsLoadingState() {
  return (
    <div className="flex items-center justify-center py-10">
      <div className="size-5 animate-spin rounded-full border-2 border-neutral-700 border-t-neutral-300" />
    </div>
  );
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}

function Section({
  icon,
  title,
  description,
  trailing,
  children,
}: SectionProps) {
  return (
    <section className="rounded-2xl border border-neutral-800/70 bg-neutral-900/40 backdrop-blur-sm">
      <header className="flex items-start gap-4 px-6 pt-6 pb-5">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-neutral-800/80 text-neutral-200 ring-1 ring-inset ring-neutral-700/60">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-white">{title}</h2>
            {trailing}
          </div>
          <p className="mt-1 text-sm leading-relaxed text-neutral-400">
            {description}
          </p>
        </div>
      </header>
      <div className="border-t border-neutral-800/70 px-6 py-5">{children}</div>
    </section>
  );
}

function FallbackNotice() {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-3">
      <InfoIcon className="size-4 shrink-0 fill-current text-amber-400 mt-0.5" />
      <p className="text-xs leading-relaxed text-amber-200/80">
        These settings apply to moderators who are not assigned to a preset.
        Moderators with a preset use their preset's configuration instead.
      </p>
    </div>
  );
}

export function HubspotTicketConfigurePage() {
  const { authState, canRender } = useRequireAuth({ requireRole: 'admin' });
  const trpc = use(TRPCContext);
  const {
    data: settings,
    setData: setSettings,
    isLoading,
    invalidate,
    run,
  } = useAsyncData<ModeratorSettingsResponse | null>({
    initialData: null,
    initialLoading: true,
  });
  const [selectedDappIds, setSelectedDappIds] = useState<string[]>([]);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  usePageTitle('Configure Tickets | Publishing Portal');

  const dappChoices = (settings?.dapps ?? []).map((dapp) => ({
    key: dapp.id,
    label: dapp.packageName
      ? `${dapp.dappName} (${dapp.packageName})`
      : dapp.dappName,
  }));

  const hasChanges = (() => {
    if (!settings) {
      return false;
    }

    const baselineHidden = new Set(settings.hiddenDappIds);
    const currentHidden = new Set(selectedDappIds);

    if (baselineHidden.size !== currentHidden.size) {
      return true;
    }

    for (const id of baselineHidden) {
      if (!currentHidden.has(id)) {
        return true;
      }
    }

    return (
      (settings.rejectionFailureAssigneeUserId ?? '') !== selectedAssigneeId
    );
  })();

  useEffect(() => {
    invalidate();

    if (!canRender) {
      return;
    }

    void run(
      () => trpc.client.getHubspotTicketModeratorSettings.query(undefined),
      {
        onSuccess: (result, helpers) => {
          if (isRight(result)) {
            const nextSettings = result.right as ModeratorSettingsResponse;

            helpers.setData(nextSettings);
            setSelectedDappIds(nextSettings.hiddenDappIds);
            setSelectedAssigneeId(
              nextSettings.rejectionFailureAssigneeUserId ?? '',
            );
            return;
          }

          if (isLeft(result)) {
            toast.error(result.left.message);
          }
        },
        onError: (error) => {
          captureAndReportError(error, {
            operation: 'admin.loadTicketModeratorSettings',
            component: 'HubspotTicketConfigurePage',
          });
          toast.error('Failed to load ticket settings');
        },
      },
    );
  }, [canRender, invalidate, run, trpc.client]);

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const result =
        await trpc.client.updateHubspotTicketModeratorSettings.mutate({
          hiddenDappIds: selectedDappIds,
          rejectionFailureAssigneeUserId: selectedAssigneeId || null,
        });

      if (isRight(result)) {
        const nextSettings = result.right as ModeratorSettingsResponse;

        setSettings(nextSettings);
        setSelectedDappIds(nextSettings.hiddenDappIds);
        setSelectedAssigneeId(
          nextSettings.rejectionFailureAssigneeUserId ?? '',
        );
        toast.success('Fallback settings saved');
        return;
      }

      if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch (error) {
      captureAndReportError(error, {
        operation: 'admin.saveTicketModeratorSettings',
        component: 'HubspotTicketConfigurePage',
      });
      toast.error('Failed to save fallback settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (!canRender || authState.type !== AuthStateType.Authenticated) {
    return null;
  }

  const source = settings?.rejectionFailureAssigneeSource ?? 'unset';
  const sourceStyle = sourceStyles[source];
  const hiddenCount = selectedDappIds.length;
  const totalApps = settings?.dapps.length ?? 0;
  const assignableUsers = settings?.assignableUsers ?? [];
  const fallbackEmail = settings?.fallbackRejectionFailureAssigneeEmail ?? '';

  return (
    <ManagementPageLayout
      authState={authState}
      canAccess={canAccessAdminPages}
      className="flex-1 overflow-y-auto p-4 md:p-8"
      maxWidthClassName="max-w-4xl"
    >
      <div className="space-y-8">
        <div className="space-y-5">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="-ml-2 h-8 px-2 text-neutral-400 hover:text-white"
          >
            <Link to="/tickets">
              <ArrowBackIcon className="size-4 fill-current" />
              Tickets
            </Link>
          </Button>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-neutral-500">
              <TuneIcon className="size-3.5 fill-current" />
              Configuration
            </div>
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              Ticket settings
            </h1>
            <p className="max-w-xl text-sm text-neutral-400">
              Manage moderator presets and configure fallback settings for
              unassigned moderators.
            </p>
          </div>
        </div>

        <Section
          icon={<AdminPanelSettingsIcon className="size-5 fill-current" />}
          title="Moderator Presets"
          description="Create preset roles with custom capabilities, app blacklists, and rejection routing. Assign presets to moderators from the user management page."
        >
          <ModeratorPresetManagementSection />
        </Section>

        <Section
          icon={<RuleIcon className="size-5 fill-current" />}
          title="Rejection reasons"
          description="The taxonomy reviewers select from when rejecting a submission. Deactivated reasons stay attached to historical rejections but cannot be selected for new ones."
        >
          <RejectionReasonManagementSection />
        </Section>

        <div className="space-y-6">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold text-white">
              Global Fallback Settings
            </h2>
            <p className="text-sm text-neutral-400">
              These settings apply when a moderator does not have a preset
              assigned.
            </p>
          </div>

          <Section
            icon={<VisibilityOffIcon className="size-5 fill-current" />}
            title="Hidden apps"
            description="Tickets for selected apps are hidden from unassigned moderators. Moderators with a preset use their preset's hidden app list instead."
            trailing={
              !isLoading && (
                <span className="inline-flex items-center gap-1 rounded-full bg-neutral-800/80 px-2 py-0.5 text-xs font-medium text-neutral-300 ring-1 ring-inset ring-neutral-700/60">
                  {hiddenCount}
                  <span className="text-neutral-500">/ {totalApps}</span>
                </span>
              )
            }
          >
            {isLoading ? (
              <SettingsLoadingState />
            ) : (
              <div className="space-y-3">
                <FallbackNotice />
                <label
                  htmlFor="hidden-apps"
                  className="block text-xs font-medium uppercase tracking-wide text-neutral-500"
                >
                  Hidden apps
                </label>
                <SearchableMultiSelect
                  name="hidden-apps"
                  choices={dappChoices}
                  value={selectedDappIds}
                  placeholder="Select apps to hide"
                  searchPlaceholder="Search apps..."
                  maxVisibleSelections={8}
                  disabled={isSaving}
                  onChange={setSelectedDappIds}
                />
                <p className="text-xs text-neutral-500">
                  {hiddenCount === 0
                    ? 'All apps are visible to unassigned moderators.'
                    : `${hiddenCount} ${hiddenCount === 1 ? 'app is' : 'apps are'} hidden from unassigned moderators.`}
                </p>
              </div>
            )}
          </Section>

          <Section
            icon={<AltRouteIcon className="size-5 fill-current" />}
            title="Rejection routing"
            description="Fallback destination for soft-rejections from moderators whose preset does not configure a rejection route."
            trailing={
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${sourceStyle.ring} ${sourceStyle.text}`}
              >
                <span className={`size-1.5 rounded-full ${sourceStyle.dot}`} />
                {sourceStyle.label}
              </span>
            }
          >
            {isLoading ? (
              <SettingsLoadingState />
            ) : (
              <div className="space-y-5">
                <FallbackNotice />
                <div className="space-y-3">
                  <label
                    htmlFor="rejection-route-assignee"
                    className="block text-xs font-medium uppercase tracking-wide text-neutral-500"
                  >
                    Route rejected reviews to
                  </label>
                  <select
                    id="rejection-route-assignee"
                    value={selectedAssigneeId}
                    disabled={isSaving}
                    onChange={(event) =>
                      setSelectedAssigneeId(event.target.value)
                    }
                    className="w-full rounded-lg border border-neutral-700/70 bg-neutral-800/60 px-3 py-2.5 text-sm text-white transition-colors hover:border-neutral-600 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="" className="bg-neutral-900 text-white">
                      Use fallback assignment
                    </option>
                    {assignableUsers.map((user) => (
                      <option
                        key={user.id}
                        value={user.id}
                        className="bg-neutral-900 text-white"
                      >
                        {user.fullName} ({user.email}, {roleLabel[user.role]})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center justify-between gap-4 rounded-lg border border-neutral-800/80 bg-neutral-900/60 px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                      Fallback assignee
                    </div>
                    <div className="mt-0.5 truncate font-mono text-sm text-neutral-200">
                      {fallbackEmail || '—'}
                    </div>
                  </div>
                  <span className="shrink-0 text-xs text-neutral-500">
                    Used when no assignee is configured
                  </span>
                </div>
              </div>
            )}
          </Section>

          <div className="flex items-center justify-end gap-3">
            {hasChanges && !isLoading && (
              <span className="inline-flex items-center gap-1.5 text-xs text-amber-300/90">
                <span className="size-1.5 animate-pulse rounded-full bg-amber-400" />
                Unsaved changes
              </span>
            )}
            <Button
              type="button"
              className="bg-teal-600 text-white shadow-sm shadow-teal-950/40 hover:bg-teal-500 disabled:bg-neutral-800 disabled:text-neutral-500"
              disabled={isLoading || isSaving || !hasChanges}
              onClick={handleSave}
            >
              <SaveIcon className="size-4 fill-current" />
              {isSaving ? 'Saving…' : 'Save fallback settings'}
            </Button>
          </div>
        </div>
      </div>
    </ManagementPageLayout>
  );
}
